import type { RunStatus, StepCaptions } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'

/**
 * `chat` — the conversation surface: an answer, what it cited, how it was made, and the
 * room the three of them sit in.
 *
 * Moved here from `components/chat/answer-strings.ts` plus the strings that never reached a
 * catalogue at all and were stranded in `ChatPage.tsx` (PRD-27 F-14, LOC-010). The split
 * that produced two sibling files — `answer-strings` for what the machine tells the reader,
 * `feedback-strings` for what the reader tells the owner — survives as two namespaces.
 *
 * ── Every string here names an OUTCOME ──
 *
 * The conversation column is the one surface where that is mechanically enforced:
 * `marketplace.e2e.ts` asserts `<main>` contains nothing matching
 * `/balanced|thorough|answer mode|grounded/i`, because a control or a badge that describes
 * how the machine was configured invites a reader to argue with it (AGM-009/AGM-022). The
 * mechanism vocabulary lives in the `trace` namespace, which is read by whoever operates the
 * deployment.
 *
 * That split is a translation instruction and not only an English one. The two halves of a
 * fused search are called "wording" and "meaning" here and "lexical"/"semantic" in `trace`;
 * Portuguese keeps exactly the same distance between the two registers. A reader deciding
 * whether to trust a passage needs to know it was found by the words it uses or by what it
 * is about, and neither of those is a term of art.
 *
 * English is the source of truth AND the type (LOC-011): `Messages = typeof en`, so the
 * Portuguese sibling is checked against the shape of this file and a missing key is a
 * compile error rather than a runtime fallback.
 *
 * Anything that interpolates is a FUNCTION returning a whole sentence, never a template
 * assembled at the call site (LOC-013).
 */

/**
 * The slice of `useI18n()` a counting entry here needs, threaded in from the component.
 *
 * Structural rather than `Pick<I18n, …>`: this file is imported by `messages/en/index.ts`,
 * which `I18nProvider` imports in turn, so naming `I18n` here would close that circle for no
 * gain. What a counting sentence actually needs is a locale.
 */
type Counting = { locale: Locale }

/**
 * Picks the form CLDR says this count takes.
 *
 * Never `count === 1`: `Intl.PluralRules('pt-BR').select(0)` returns `one`, so Portuguese
 * takes the singular at zero ("0 trecho") while English takes the plural ("0 passages"). A
 * two-branch comparison is therefore already wrong in one of the two shipped languages, on
 * every empty state, silently (PRD-27 D-19).
 */
const plural = (
  fmt: Counting,
  count: number,
  forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }
): string => forms[pluralCategory(fmt.locale, count)] ?? forms.other

export const chat = {
  // --- an answer, and the mark on it --------------------------------------

  /** The byline above an answer. The agent's own name is data and arrives as a parameter (D-17). */
  answeredBy: (agentName: string): string => `${agentName} answered`,
  /**
   * The trust mark on an answer that cites something. Present only when there is at least one
   * citation, so it is a statement about this answer rather than about the agent's settings —
   * and it is never the only carrier of that fact, since the sources are listed underneath.
   */
  cited: 'from documents',
  /** The citation chip's tooltip. Its accessible NAME stays the bare marker number — the
   *  suite finds the chip by `{ name: '1', exact: true }`, and a helpful `sr-only` prefix
   *  would break both browser suites. */
  citationTitle: (documentTitle: string, locator: string, supported: boolean): string =>
    supported ? `${documentTitle} — ${locator}` : `${documentTitle} — ${locator} (support not verified)`,

  /** Exact: `workspace.e2e.ts` finds the source row by this word alone. */
  sources: 'Sources',
  sourceUnverified: 'unverified',
  sourceStale: 'stale',
  sourceOpen: (documentTitle: string, locator: string): string => `${documentTitle} — ${locator}`,

  /** Exact, and distinct: the suite tells the panel's close button from the backdrop only by exactness. */
  drawerTitle: 'Source detail',
  /**
   * Deliberately NOT `common.close`, even though the word is the same in both locales today.
   * The contract this entry carries is that it differs from `drawerCloseBackdrop`, and a
   * change to the shared word must not be able to collapse the two into one accessible name.
   */
  drawerClose: 'Close',
  drawerCloseBackdrop: 'Close source detail',
  drawerPassage: 'Retrieved passage',
  drawerMeta: (sourceName: string, locator: string, score: string): string =>
    `${sourceName} · ${locator} · relevance ${score}`,
  drawerUnsupported:
    'This passage was retrieved, but the wording of the claim it supports could not be matched against it. Read the passage before relying on the sentence it is attached to.',
  /**
   * `when` is `formatAgo` output, which Portuguese renders WITH its own preposition ("há 5
   * min.") and without one on the absolute fallback ("20 de set."). A Portuguese sentence
   * that added a second preposition would read "indexado em há 5 min." on one branch and
   * correctly on the other, so the Portuguese entry uses a colon and carries none (LOC-022).
   */
  drawerIndexed: (when: string, stale: boolean): string =>
    stale ? `indexed ${when} · past this source’s freshness target` : `indexed ${when}`,

  // --- provenance: the reader's account of the run -------------------------

  provenanceTitle: 'How this was made',
  /** Screen-reader names for the three totals; the values themselves are the visible part. */
  totalTime: 'time taken',
  /**
   * Not "tokens": this label is read aloud inside the conversation column, where the copy
   * rules reserve mechanism vocabulary for the operator-facing trace panel. The number itself
   * is unchanged — only what it is called to someone who cannot see the icon beside it. The
   * Portuguese must hold the same line, and `token` being a D-15 term of art does NOT license
   * it here: the English chose not to say it either.
   */
  totalTokens: 'how much it read and wrote',
  totalCost: 'cost',

  phaseModel: 'thinking',
  phaseWriting: 'writing',
  phaseRetrieval: 'looking things up',
  phaseTool: 'doing something',
  phaseApproval: 'waiting for you',

  /**
   * A run status as a word, never as its identifier (PRD-27 D-9, LOC-018).
   *
   * `satisfies` over the contract enum is the whole mechanism: a new member is a compile
   * error here rather than `waiting_for_external` in a badge. The English is exactly what the
   * underscore-stripping this replaced produced, so nothing on screen moved.
   *
   * The same eleven values are worded again in `trace.runStatus`, on purpose (D-9 as
   * amended): a label map lives in the namespace of the surface that renders it, so the two
   * registers can diverge if they ever need to, and neither namespace's translator has to
   * edit a file nine other people are editing.
   *
   * For Portuguese this map is single-gendered and that is load-bearing: every one of these
   * describes an `execução`, which is FEMININE — "concluída", never "concluído".
   */
  runStatus: {
    queued: 'queued',
    running: 'running',
    waiting_for_approval: 'waiting for approval',
    waiting_for_input: 'waiting for input',
    waiting_for_external: 'waiting for external',
    suspended: 'suspended',
    completed: 'completed',
    partially_completed: 'partially completed',
    failed: 'failed',
    cancelled: 'cancelled',
    expired: 'expired'
  } satisfies Record<RunStatus, string>,

  /** The tape is a picture of one sentence, so the sentence is what a screen reader gets. */
  tapeSummary: (spans: string): string => `Where the time went: ${spans}.`,
  tapeSpan: (phase: string, duration: string): string => `${phase} ${duration}`,
  /** The hover title on one segment. A separate entry from `tapeSpan`: the separator differs. */
  tapeSpanTitle: (phase: string, duration: string): string => `${phase} · ${duration}`,
  tapeHint: 'width = time spent',

  showWork: 'Show the work',
  hideWork: 'Hide the work',

  evidenceTitle: 'Evidence',
  /**
   * The two ends of the rank bar. "wording" and "meaning" rather than lexical and semantic —
   * see the register note at the top of this file; `trace.retrievalLexical` is where the
   * terms of art are allowed.
   */
  evidenceScale: 'wording ◂ ▸ meaning',
  evidenceUncited: '—',
  evidenceRank: (rank: number | null | undefined): string => (rank == null ? '—' : `#${rank}`),

  stepsTitle: 'Steps',
  /**
   * What a step DID, for the reader (PRD-27 D-8, LOC-042).
   *
   * The same six codes are worded again in `trace.stepLabel`, and the two must not converge.
   * That one is the operator's: it says "Reasoning with balanced", naming the model profile
   * the deployment chose. This one may not, and not merely by taste — `<main>` on this route
   * is asserted to contain nothing matching `/balanced|thorough|answer mode|grounded/i`, and
   * the alias is exactly the kind of thing a reader would start arguing with instead of
   * reading the answer (AGM-022). So `model_call` takes the alias and says nothing about it.
   *
   * Present participles, because both places that render this are describing work in
   * progress or work just done, beside a spinner or inside a chip — never a heading.
   */
  stepActivity: {
    model_call: () => 'thinking',
    knowledge_search: () => 'looking things up',
    memory_write: () => 'saving a preference',
    announcement: () => 'sending an announcement',
    delegation: () => 'asking another agent',
    /**
     * A tool this platform does not ship. Its name comes from the server that provides it, so
     * it is data: shown verbatim, in either language, exactly as the panel shows it now (D-17).
     */
    tool_call: ({ tool }) => tool
  } satisfies StepCaptions,

  /** How many passages a step kept. */
  stepPassages: (fmt: Counting, kept: number): string =>
    plural(fmt, kept, { one: `${kept} passage used`, other: `${kept} passages used` }),
  /** A step that did not finish. Its subject is an `etapa`, which is feminine in Portuguese. */
  stepFailed: 'failed',
  /**
   * Only ever rendered when the run really took no action. "Nothing was sent anywhere" is a
   * stronger claim than this deployment can make from the client, so it is not made.
   */
  noActions: 'No actions were taken.',

  inspect: 'show trace',
  inspecting: 'showing trace',

  /**
   * After Stop. The client asks the server to cancel and settles its own state either way, so
   * this says what the reader did rather than promising what the run then did.
   */
  stopped: 'You stopped this answer.',

  // --- the conversation page itself ---------------------------------------
  // Stranded in `ChatPage.tsx` until PRD-27 (F-14). Everything a person reads on this route
  // that is not an answer, a source or a run.

  noConversationTitle: 'No conversation selected',
  noConversationBody: 'Pick a conversation from the left, or start a new one.',
  /**
   * The stand-in when the session has no display name yet. It is a word in a greeting, not a
   * name, so it translates (D-13's rule, applied to a placeholder rather than to a brand):
   * reading it as a name is how the sign-in screen ends up saying "Olá, there."
   */
  greetingFallbackName: 'there',

  /** Before the first token, while the run says it is deliberating. */
  thinking: 'thinking…',
  /**
   * Before the first step has a caption of its own — the half-second before one exists — and
   * also for a step whose code this build cannot render, since the server's English `label`
   * names the model profile and is not something this column says (D-8, AGM-022).
   */
  starting: 'starting…',
  /**
   * The lead-in to a failed run. What follows it is the failure's own sentence, rendered from
   * its `messageKey` when it carries one and from the server's English when it does not —
   * which is what `describeRunFailure` decides, never this entry (PRD-27 D-4, LOC-033).
   */
  runFailed: 'The run did not finish.',

  /** The badge on an agent an administrator has confirmed. Lowercase: it sits in a 9px pill. */
  official: 'official',
  /**
   * The visible word of the About link. Its accessible name is the whole phrase, which lives
   * in `marketplace.chat.aboutLink` — two entries rather than one because the pill is 33px
   * and the screen reader is not (LOC-014).
   */
  aboutShort: 'about',

  composerLabel: 'Message',
  /** Reads "⏎ to send": the key is an icon, so the catalogue owns only the words after it. */
  composerSendHint: 'to send',
  sendMessage: 'Send message',
  stopGenerating: 'Stop generating',

  showTrace: 'Show trace',
  hideTrace: 'Hide trace'
}
